import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const inputCls = "mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs";

export default function BulkEditDialog({ open, onOpenChange, ids, options, onDone }) {
  const [form, setForm] = useState({ cost_code_id: "", cost_type_id: "", vendor: "", markup_pct: "" });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const apply = async () => {
    setBusy(true);
    try {
      const payload = { ids };
      if (form.cost_code_id) payload.cost_code_id = form.cost_code_id;
      if (form.cost_type_id) payload.cost_type_id = form.cost_type_id;
      if (form.vendor) payload.vendor = form.vendor;
      if (form.markup_pct !== "") payload.markup_pct = parseFloat(form.markup_pct);
      const res = await estApi.bulkEdit(payload);
      toast.success(`${res.updated} rows updated${res.price_changes ? `, ${res.price_changes} price changes logged` : ""}`);
      setConfirming(false);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk edit failed");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const nothingSet = !form.cost_code_id && !form.cost_type_id && !form.vendor && form.markup_pct === "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Bulk edit {ids.length} item{ids.length === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>Blank fields are left untouched.</DialogDescription>
          </DialogHeader>

          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Cost code</span>
            <select value={form.cost_code_id} onChange={(e) => set("cost_code_id", e.target.value)}
                    className={inputCls} data-testid="bulk-cost-code">
              <option value="">— leave unchanged —</option>
              {(options?.cost_codes || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} {c.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Cost type</span>
            <select value={form.cost_type_id} onChange={(e) => set("cost_type_id", e.target.value)}
                    className={inputCls} data-testid="bulk-cost-type">
              <option value="">— leave unchanged —</option>
              {(options?.cost_types || []).map((t) => (
                <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Vendor</span>
            <input value={form.vendor} onChange={(e) => set("vendor", e.target.value)}
                   className={inputCls} data-testid="bulk-vendor" />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Markup % (adjusts unit cost)
            </span>
            <input type="number" step="0.1" value={form.markup_pct}
                   onChange={(e) => set("markup_pct", e.target.value)}
                   placeholder="e.g. 5 raises every selected cost by 5%"
                   className={inputCls} data-testid="bulk-markup" />
            <span className="mt-0.5 block text-[10px] text-slate-400">
              Every change is written to price history.
            </span>
          </label>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                    data-testid="bulk-cancel-btn">Cancel</Button>
            <Button size="sm" disabled={nothingSet || busy} onClick={() => setConfirming(true)}
                    data-testid="bulk-apply-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply to {ids.length} item{ids.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This updates the cost library itself. Open estimates keep the cost they were built with.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={apply} data-testid="bulk-confirm-ok">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Yes, update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
